/* eslint-disable @typescript-eslint/no-explicit-any */

import { AuthSession, JWT, fetchAuthSession } from "@aws-amplify/core";
import {
  FederationProvidersEnum,
  IOrganizationsCognitoAttributeAugmented,
} from "@tedivo/tvd-api-models";
import {
  ICognitoEventClaimsBase,
  ICognitoEventClaimsForFE,
  parseCognitoClaimsForFe,
} from "./ICognitoEventClaims";
import { ResetPasswordOutput, confirmResetPassword, resetPassword } from "aws-amplify/auth";
import {
  confirmSignIn,
  fetchMFAPreference,
  getCurrentUser,
  setUpTOTP,
  signIn,
  signOut,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
} from "@aws-amplify/auth";
import { getPreferencesValue, setPreferencesKeyAndValue } from "@tedivo/tedivo-preferences";

import { Amplify } from "aws-amplify";
import { AuthMFAType } from "@aws-amplify/auth/dist/esm/types";
import { errorTracking } from "../tracking/errorTracking";
import goSquared from "../tracking/goSquared";

const TypedTvdAwsCognitoEventTarget = EventTarget as {
  new (): ITvdAwsCognitoTarget;
  prototype: ITvdAwsCognitoTarget;
};

export const FEDERATION_PROVIDERS: Record<FederationProvidersEnum & string, string> = {
  Microsoft: "MicrosfotEntraIdSaml",
  Google: "",
};

/**
 * Class for AWSCognito API calls using AWS Amplify
 */
class TvdAwsCognito extends TypedTvdAwsCognitoEventTarget {
  private currentCognitoSession?: AuthSession = undefined;
  private currentEmail?: string = undefined;
  private previousEmail?: string = undefined;

  private _cognitoEventClaims?: ICognitoEventClaimsForFE = undefined;
  private _currentOrgSecurityData?: IOrganizationsCognitoAttributeAugmented = undefined;
  private _userEditableDetails: {
    name: string;
    familyName: string;
    email: string;
  } = {
    name: "",
    familyName: "",
    email: "",
  };

  private jwtToken?: string = undefined;
  private accessTokenInternal?: string = undefined;
  private refreshTokenState: "idle" | "refreshing" = "idle";

  public orgUsersBySub: Record<string, string> | undefined = undefined;

  private previouslyLoggedIn = false;

  private _mfaEnabled: AuthMFAType | undefined = undefined;
  public get isMfaEnabled() {
    return this._mfaEnabled;
  }

  public setMfaFlagAsDisabled = () => {
    this._mfaEnabled = undefined;
  };

  private configUserPoolId = process.env.NX_PUBLIC_USER_POOL_ID || "";
  private configClientId = process.env.NX_PUBLIC_USER_APP_CLIENT_ID || "";

  constructor() {
    super();
    const stage = process.env.NX_PUBLIC_STAGE;
    if (stage === "dev" || stage === "alpha") window.awsCognito = this;

    // Configure Amplify Auth
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: this.configUserPoolId,
          userPoolClientId: this.configClientId,
        },
      },
    });
  }

  public get isLoggedIn() {
    return !!this.currentCognitoSession;
  }

  public get userSub(): string | undefined {
    return this._cognitoEventClaims?.sub;
  }

  public get email() {
    return this.currentEmail;
  }

  get userMarketingMails(): boolean {
    return this._cognitoEventClaims?.["custom:marketing"] || false;
  }

  get userEditableDetails() {
    return { ...this._userEditableDetails };
  }

  // #region Auth tokens info
  public get sessionExpires(): Date | null {
    const dtStr = this._cognitoEventClaims?.exp;
    return dtStr ? new Date(dtStr) : null;
  }

  public get idToken(): string | undefined {
    return this.jwtToken;
  }

  public get accessToken(): string | undefined {
    return this.accessTokenInternal;
  }
  // #endregion

  // #region Current Organization data
  public get currentOrganizationId(): string | undefined {
    return this._currentOrgSecurityData?.orgId;
  }

  public get currentOrgSecurityData(): IOrganizationsCognitoAttributeAugmented | undefined {
    return this._currentOrgSecurityData;
  }

  get orgAllowedDomains(): string[] {
    return (this._currentOrgSecurityData?.domains || "").split(",") || [];
  }

  get orgMfaForAllUsers(): boolean {
    return this._currentOrgSecurityData?.mfaAll === "1" || false;
  }

  set orgMfaForAllUsers(v: boolean) {
    if (!this._currentOrgSecurityData) return;
    this._currentOrgSecurityData.mfaAll = v ? "1" : "0";
  }

  get isLoginInfoComplete(): boolean {
    return !!this._cognitoEventClaims;
  }

  get allOrganizationsInfo(): IOrganizationsCognitoAttributeAugmented[] {
    return this._cognitoEventClaims?.organizations || [];
  }

  get allEffectiveOrganizationsInfo(): IOrganizationsCognitoAttributeAugmented[] {
    return (
      this._cognitoEventClaims?.organizations.filter(
        (o) => o.uEnabled === "1" && o.oEnabled === "1",
      ) || []
    );
  }

  // #endregion

  // private get accountExpirationDate(): Date | undefined {
  //   const expiresAt =
  //     this._cognitoEventClaims?.orgAdminOptions?.subscription?.expiresAt;

  //   if (typeof expiresAt === "string") return new Date(expiresAt as string);
  //   else if (expiresAt) return expiresAt;

  //   return undefined;
  // }

  public getEmail() {
    return this.currentEmail || "";
  }

  public changeCurrentOrganization = (orgId: string): boolean => {
    if (orgId === this._currentOrgSecurityData?.orgId) return false;

    const orgRel = this._cognitoEventClaims?.organizations.find(
      (o) => o.orgId === orgId && o.uEnabled === "1",
    );

    if (orgRel) {
      this._currentOrgSecurityData = orgRel;
      window.setTimeout(() => {
        this.dispatchEvent(new CustomEvent("userDataRetrieved", { detail: orgRel }));
      }, 0);
      return true;
    } else {
      console.error("Unable to set invalid organization", orgId);
      return false;
    }
  };

  public setUserSessionFromTokens(session: AuthSession) {
    const idToken = session.tokens?.idToken;

    if (idToken && this.isIdTokenValid(idToken)) {
      this.currentCognitoSession = session;

      this.currentEmail = idToken.payload.email as string;
      this.previousEmail = this.currentEmail;

      this.jwtToken = idToken.toString();
      this.accessTokenInternal = session.tokens?.accessToken?.toString();

      const okCb = this._onSuccess();
      okCb(session);
    } else {
      this.dispatchEvent(
        new CustomEvent("authenticationChanged", {
          detail: { isLoggedIn: false },
        }),
      );
    }
  }

  public isUsingFederatedIdentity() {
    const fedIdentities = this._cognitoEventClaims?.identities || [];
    return fedIdentities.length > 0;
  }

  public getRedirectFederationLoginUrl(federation: FederationProvidersEnum) {
    if (!federation || !FEDERATION_PROVIDERS[federation]) return "";

    const env = process.env.NX_PUBLIC_STAGE || "dev";
    const region = this.configUserPoolId.split("_")[0];
    const origin = window.location.origin;
    const url = [
      `https://tvd-users-api-tvd-stack-${env}.auth.${region}.amazoncognito.com/oauth2/authorize`,
      `?identity_provider=${FEDERATION_PROVIDERS[federation]}`,
      `&redirect_uri=${origin}`,
      `&response_type=CODE`,
      `&client_id=${this.configClientId}`,
      `&scope=email%20openid%20phone`,
    ].join("");

    return url;
  }

  /**
   * This is only used the first time the browser loads, to see if there is an existing valid session
   */
  public async checkIfUserIsLoggedIn(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens) {
        this.clearSession(false);
        return false;
      }

      this.currentCognitoSession = session;
      this.jwtToken = session.tokens.idToken?.toString();
      this.accessTokenInternal = session.tokens.accessToken?.toString();

      // Get user attributes
      const user = await getCurrentUser();
      if (user) {
        this.currentEmail = user.signInDetails?.loginId;
        this.previousEmail = this.currentEmail;
      }

      const proccessed = this.processCognitoSessionClaimsForFE(session.tokens.idToken);

      // getMfaOptions reads _cognitoEventClaims, which is populated by
      // processCognitoSessionClaimsForFE above. Calling it before would always
      // see undefined claims on a page refresh and skip the MFA check entirely.
      if (this._cognitoEventClaims && !this.isUsingFederatedIdentity()) this.getMfaOptions();

      if (proccessed === "OK") {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: true },
          }),
        );
        return true;
      } else if (proccessed === "FAIL") {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: false },
          }),
        );
        this.clearSession();
        return false;
      } else {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: true, error: "No organizations found" },
          }),
        );
        return false;
      }
    } catch (error) {
      console.log("No session found", error);
      this.clearSession();
      this.dispatchEvent(
        new CustomEvent("authenticationChanged", {
          detail: { isLoggedIn: false },
        }),
      );
      return false;
    }
  }

  /**
   * Authenticates a user in Cognito using Custom Auth flow without SRP
   * This sends username and code for authentication
   */
  public async authenticateUserWithCode(username: string, code: string): Promise<IAwsAuthResponse> {
    try {
      if (!username || !code) {
        return { code: "loginMissingData" };
      }

      // Initiate custom auth flow without SRP
      const result = await signIn({
        username,
        options: {
          authFlowType: "CUSTOM_WITHOUT_SRP",
        },
      });

      // If already signed in, process session
      if (result.isSignedIn) {
        const session = await fetchAuthSession();
        const proccessed = this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

        if (proccessed === "OK" || proccessed === "NO_ORGS") {
          this.jwtToken = session.tokens?.idToken?.toString();
          this.accessTokenInternal = session.tokens?.accessToken?.toString();

          setPreferencesKeyAndValue("currentUsername", username);

          this.currentEmail = username;
          this.previousEmail = this.currentEmail;
          this.currentCognitoSession = session;

          this.dispatchEvent(
            new CustomEvent("authenticationChanged", {
              detail: { isLoggedIn: true },
            }),
          );

          if (proccessed === "OK") {
            return {
              code: "ok",
              accessToken: this.accessTokenInternal,
              idToken: this.jwtToken,
            };
          } else {
            return {
              code: "failure",
              error: { message: "No organizations found" },
            };
          }
        } else {
          return {
            code: "failure",
            error: { message: "errors:incorrectUsernamePassword" },
          };
        }
      }

      // If challenge is required, confirm sign in with code
      if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
        const confirmResult = await confirmSignIn({
          challengeResponse: code,
        });

        if (confirmResult.isSignedIn) {
          const session = await fetchAuthSession();
          const proccessed = this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

          if (proccessed === "OK" || proccessed === "NO_ORGS") {
            this.jwtToken = session.tokens?.idToken?.toString();
            this.accessTokenInternal = session.tokens?.accessToken?.toString();

            this.currentEmail = username;
            this.previousEmail = this.currentEmail;
            this.currentCognitoSession = session;

            setPreferencesKeyAndValue("currentUsername", username);

            this.dispatchEvent(
              new CustomEvent("authenticationChanged", {
                detail: { isLoggedIn: true },
              }),
            );

            if (proccessed === "OK") {
              return {
                code: "ok",
                accessToken: this.accessTokenInternal,
                idToken: this.jwtToken,
              };
            } else {
              return {
                code: "failure",
                error: { message: "No organizations found" },
              };
            }
          } else {
            return {
              code: "failure",
              error: { message: "errors:incorrectUsernamePassword" },
            };
          }
        } else {
          return {
            code: "customChallenge",
          };
        }
      }

      return {
        code: "customChallenge",
      };
    } catch (error: any) {
      console.log("Custom auth failed", error);

      if (error && error.code === "UserLambdaValidationException") {
        error.message = error.message.split("enabled=").pop().replace(/\.$/, "");
      }

      return { code: "failure", error };
    }
  }

  /**
   * Authenticates a user in Cognito using Amplify Auth
   */
  public async authenticateUser(username: string, password: string): Promise<IAwsAuthResponse> {
    try {
      if (!username || !password) {
        return { code: "loginMissingData" };
      }

      const result = await signIn({ username, password });

      if (result.isSignedIn) {
        const session = await fetchAuthSession();

        const proccessed = this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

        if (proccessed === "OK" || proccessed === "NO_ORGS") {
          this.jwtToken = session.tokens?.idToken?.toString();
          this.accessTokenInternal = session.tokens?.accessToken?.toString();

          this.currentEmail = username;
          this.previousEmail = this.currentEmail;
          this.currentCognitoSession = session;

          this.dispatchEvent(
            new CustomEvent("authenticationChanged", {
              detail: { isLoggedIn: true },
            }),
          );

          if (proccessed === "OK") {
            if (!this.isUsingFederatedIdentity()) this.getMfaOptions();

            return {
              code: "ok",
              accessToken: this.accessTokenInternal,
              idToken: this.jwtToken,
            };
          } else {
            return {
              code: "failure",
              error: { message: "No organizations found" },
            };
          }
        } else {
          return {
            code: "failure",
            error: { message: "errors:incorrectUsernamePassword" },
          };
        }
      } else {
        if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
          return { code: "newPasswordRequired", oldPassword: password };
        } else if (result.nextStep.signInStep === "RESET_PASSWORD") {
          return { code: "resetPassword" };
        } else if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
          return { code: "totpRequired" };
        } else if (result.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
          return { code: "customChallenge" };
        } else {
          // NOTHING
          this.setMfaFlagAsDisabled();
          this.currentEmail = undefined;
          this.previousEmail = this.currentEmail;
          this.currentCognitoSession = undefined;

          this.dispatchEvent(
            new CustomEvent("authenticationChanged", {
              detail: { isLoggedIn: false },
            }),
          );

          return {
            code: "failure",
            error: { message: "Failed to compose user data" },
          };
        }
      }
    } catch (error: any) {
      console.log("Authentication failed", error);

      if (error && error.code === "UserLambdaValidationException") {
        error.message = error.message.split("enabled=").pop().replace(/\.$/, "");
      }

      return { code: "failure", error };
    }
  }

  public async setNewPasswordFromForceChange(
    username: string,
    newPassword: string,
  ): Promise<IAwsAuthResponse> {
    try {
      if (!username) {
        throw { code: "notLoggedIn" };
      }

      const resp = await confirmSignIn({ challengeResponse: newPassword });

      if (resp.isSignedIn) {
        const session = await fetchAuthSession();
        this.currentCognitoSession = session;
        this.jwtToken = session.tokens?.idToken?.toString();
        this.accessTokenInternal = session.tokens?.accessToken?.toString();

        this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

        return {
          code: "ok",
          idToken: this.jwtToken,
          accessToken: this.accessTokenInternal,
        };
      } else {
        throw resp.nextStep;
      }
    } catch (error: any) {
      return { code: "failure", error };
    }
  }

  public updateUserDetails(
    name: string,
    familyName: string,
    email: string,
    marketingMails: boolean | undefined = undefined,
  ) {
    if (!this._cognitoEventClaims) return;

    this._userEditableDetails.name = name;
    this._userEditableDetails.familyName = familyName;

    if (email) this._userEditableDetails.email = email;

    if (marketingMails !== undefined) this._cognitoEventClaims["custom:marketing"] = marketingMails;
  }

  public isIdTokenValid(idTokenAccessor: JWT | undefined): boolean {
    const isValid =
      !!idTokenAccessor && new Date((idTokenAccessor.payload.exp || 0) * 1000) > new Date();
    return isValid;
  }

  public processCognitoSessionClaimsForFE(
    idTokenAccessor: JWT | undefined,
  ): "OK" | "FAIL" | "NO_ORGS" {
    const isValid = this.isIdTokenValid(idTokenAccessor);

    const idTokenPayload = idTokenAccessor?.payload as ICognitoEventClaimsBase | undefined;

    if (!idTokenPayload || !isValid) {
      this._cognitoEventClaims = undefined;
      return "FAIL"; // -> Fast Return. No valid session
    }

    const cognitoClaimsForFE = parseCognitoClaimsForFe(idTokenPayload as ICognitoEventClaimsBase);

    this._cognitoEventClaims = cognitoClaimsForFE;

    this.updateUserDetails(
      cognitoClaimsForFE.name,
      cognitoClaimsForFE.family_name,
      cognitoClaimsForFE.email,
      undefined,
    );

    const effectiveOrgs = cognitoClaimsForFE.organizations.filter(
      (o) => o.uEnabled === "1" && o.oEnabled === "1",
    );

    if (effectiveOrgs.length === 0) {
      return "NO_ORGS"; // -> Fast Return. No valid session
    } else if (effectiveOrgs.length === 1) {
      // Use effectiveOrgs (enabled orgs only), not the raw organizations list
      // which may place a disabled org first.
      const defaultOrgId = effectiveOrgs[0].orgId;
      this.changeCurrentOrganization(defaultOrgId);
    } else if (effectiveOrgs.length > 1) {
      const preferredOrgId = getPreferencesValue("preferredOrgId") as string;
      if (preferredOrgId) this.changeCurrentOrganization(preferredOrgId);
    }

    // goSquared.identify() is not called here: the organization may not be resolved yet
    // (multi-org without preferredOrgId). SecurityModule does it on "userDataRetrieved".

    return "OK";
  }

  private async tryToRefreshToken(): Promise<boolean> {
    try {
      if (!this.currentCognitoSession || this.refreshTokenState === "refreshing") {
        this.refreshTokenState = "idle";
        return false;
      }

      this.refreshTokenState = "refreshing";

      const session = await fetchAuthSession({ forceRefresh: true });

      if (session.tokens) {
        this.currentCognitoSession = session;
        this.jwtToken = session.tokens.idToken?.toString();
        this.accessTokenInternal = session.tokens.accessToken?.toString();

        this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

        console.log({
          newsessionExpires: this.sessionExpires?.toISOString(),
        });

        this.refreshTokenState = "idle";
        return true;
      } else {
        this.signOut();
        this.refreshTokenState = "idle";
        return false;
      }
    } catch (error) {
      this.signOut();
      this.refreshTokenState = "idle";
      return false;
    }
  }

  public async signOut() {
    try {
      await signOut();
      this.clearSession();
      return true;
    } catch (error) {
      errorTracking.leaveBreadcrumb("Cognito signOut error", { error });
      this.clearSession();
      return true;
    }
  }

  public async changePassword(
    username: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<Error | undefined> {
    try {
      await updatePassword({ oldPassword, newPassword });
      return undefined;
    } catch (error) {
      console.error("Change password error:", error);
      return error as Error;
    }
  }

  public async resetPassword(email: string): Promise<ResetPasswordOutput | Error> {
    try {
      const output = await resetPassword({ username: email });
      return output;
    } catch (e) {
      return e as Error;
    }
  }

  public async confirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  }: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }): Promise<void | Error> {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    } catch (e) {
      return e as Error;
    }
  }

  public clearSession(fullSignOut = true) {
    if (fullSignOut) {
      signOut().catch(console.error);
    }

    this.orgUsersBySub = undefined;
    this._cognitoEventClaims = undefined;
    this.currentCognitoSession = undefined;
    this.currentEmail = undefined;
    this._currentOrgSecurityData = undefined;

    errorTracking.leaveBreadcrumb("Cognito session cleared");
    goSquared.unIdentify();

    sessionStorage.clear();

    if (fullSignOut)
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: false },
          }),
        );
        this.dispatchEvent(new CustomEvent("sessionTimedOut"));
      }, 50);
  }

  public associateMfaDevice = ({
    associateSecretCode,
    onFailure,
  }: {
    associateSecretCode: (secretCode: string) => void;
    onFailure: (err: any) => void;
  }) => {
    setUpTOTP()
      .then((result: any) => {
        associateSecretCode(result.sharedSecret);
      })
      .catch((err: any) => {
        onFailure(err);
      });

    return true;
  };

  /**
   * This is called when the user has entered the MFA code for the first time and we need to verify it
   * @param code
   * @returns
   */
  public verifyTopTCodeOnly = async (code: string): Promise<boolean> => {
    try {
      await verifyTOTPSetup({ code });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  public async updateMFAPreferenceInCognito(enable: boolean) {
    try {
      await updateMFAPreference({ totp: enable ? "ENABLED" : "DISABLED" });
      return true;
    } catch (error) {
      console.log({ error });
      return false;
    }
  }

  public verifyTopTCode = async (code: string): Promise<IAwsAuthResponse> => {
    try {
      await confirmSignIn({ challengeResponse: code });

      const session = await fetchAuthSession();
      this.currentCognitoSession = session;
      this.jwtToken = session.tokens?.idToken?.toString();
      this.accessTokenInternal = session.tokens?.accessToken?.toString();

      const proccessed = this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

      if (proccessed === "OK") {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: true },
          }),
        );
        return {
          code: "ok",
          idToken: this.jwtToken,
          accessToken: this.accessTokenInternal,
        };
      } else {
        return {
          code: "failure",
          error: { message: "Failed to process user data" },
        };
      }
    } catch (error: any) {
      return { code: "failure", error };
    }
  };

  private _onSuccess = () => (session: AuthSession) => {
    return new Promise((resolve) => {
      this.currentEmail = session.tokens?.idToken?.payload.email as string;
      this.previousEmail = this.currentEmail;

      this.currentCognitoSession = session;

      const accessToken = session.tokens?.accessToken?.toString();
      const idToken = session.tokens?.idToken?.toString();

      /* Use the idToken for Logins Map when Federating User Pools with identity pools or when passing through an Authorization Header to an API Gateway Authorizer */
      this.jwtToken = idToken;
      this.accessTokenInternal = session.tokens?.accessToken?.toString();

      const proccessed = this.processCognitoSessionClaimsForFE(session.tokens?.idToken);

      if (proccessed === "OK") {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: true },
          }),
        );
        if (!this.isUsingFederatedIdentity()) this.getMfaOptions();
        resolve({ code: "ok", accessToken, idToken });
      } else if (proccessed === "FAIL") {
        this.setMfaFlagAsDisabled();
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: false },
          }),
        );
        resolve({ code: "failure", error: "Failed to compose user data" });
      } else {
        this.dispatchEvent(
          new CustomEvent("authenticationChanged", {
            detail: { isLoggedIn: true },
          }),
        );
        resolve({ code: "failure", error: "No organizations found" });
      }
    });
  };

  /** If the user is active, try to refresh the Tokens 20 minutes before the session ends */
  public initiateRefreshToken() {
    const MIN_REFRESH_TIME = 20 * 60 * 1000; // 20 minutes before session expires, try to refresh the token if criteria is met

    const isValid = this.isIdTokenValid(this.currentCognitoSession?.tokens?.idToken);

    if (!isValid) {
      this.signOut();
      return;
    }

    const sessionTimesOutInMilliseconds = (this.sessionExpires as Date).getTime() - Date.now();

    const remaining = sessionTimesOutInMilliseconds - MIN_REFRESH_TIME;

    if (remaining > 0 || this.refreshTokenState === "refreshing") {
      return;
    }

    if (sessionTimesOutInMilliseconds < 0) {
      this.signOut();
      return;
    }

    this.tryToRefreshToken();
  }

  /** Update the name (temporarily) to show the correct Organization Name */
  public updateOrganizationName(organizationName: string) {
    if (!this.currentOrgSecurityData) return;
    this.currentOrgSecurityData.name = organizationName;
  }

  /** Ask Cognito back-end and register MFA internally */
  public async getMfaOptions() {
    try {
      if (this.isUsingFederatedIdentity()) {
        return Promise.resolve(false);
      }

      const mfaPrefs = await fetchMFAPreference();

      if (mfaPrefs) {
        this._mfaEnabled = mfaPrefs.preferred || mfaPrefs.enabled?.[0];
      } else {
        this.setMfaFlagAsDisabled();
      }
    } catch (error) {
      console.log(error);
      this.dispatchEvent(new CustomEvent("sessionTimedOut"));
    }
  }
}

/**
 * Singleton of TvdAwsCognito, an API to interact with Cognito Pool
 */
const awsCognito = new TvdAwsCognito();

export default awsCognito;

export type IAwsAuthResponse =
  | {
      code: "ok";
      accessToken: string | undefined;
      idToken: string | undefined;
    }
  | {
      code: "failure";
      error: {
        message: string;
        name?: string;
      };
    }
  | {
      code: "newPasswordRequired";
      oldPassword: string;
    }
  | {
      code: "resetPassword";
    }
  | {
      code: "loginMissingData" | "totpRequired" | "customChallenge";
    };

export interface ICreateUserProps {
  email: string;
  password: string;
  name: string;
  familyName: string;
  company?: string;
}

/* interface Error {
  name: string;
  message: string;
  row?: string;
}

function getDeviceName() {
  return `TVD${
    process.env.NX_PUBLIC_STAGE === "prod"
      ? ""
      : `-${process.env.NX_PUBLIC_STAGE}`
  }`;
} */

interface ITvdAwsCognitoEventMap {
  sessionTimedOut: Event;
  authenticationChanged: CustomEvent<{ isLoggedIn: boolean }>;
  userDataRetrieved: CustomEvent<{
    detail: IOrganizationsCognitoAttributeAugmented;
  }>;
}

interface ITvdAwsCognitoTarget extends EventTarget {
  addEventListener<K extends keyof ITvdAwsCognitoEventMap>(
    type: K,
    listener: (ev: ITvdAwsCognitoEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void;
}
